import { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getArticles } from '../api/knowledge';
import { toast } from 'sonner';
import SearchBar from '../components/knowledge/SearchBar';
import ArticleCard from '../components/knowledge/ArticleCard';
import CreateTicketModal from '../components/knowledge/CreateTicketModal';
import FloatingHelpButton from '../components/knowledge/FloatingHelpButton';
import EmptyState from '../components/knowledge/EmptyState';
import { ArticleListSkeleton } from '../components/knowledge/LoadingSkeleton';
import { Search, FileText } from 'lucide-react';

const KnowledgeSearch = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketInitial, setTicketInitial] = useState({ subject: '', description: '' });

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      const response = await getArticles({ search: query, limit: 20 });
      if (response.success) {
        setResults(response.data.articles || []);
      }
    } catch (error) {
      console.error('Search articles failed:', error);
      toast.error('Failed to search articles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicketFromSearch = () => {
    setTicketInitial({
      subject: query ? `Issue: ${query}` : 'Support Request from Knowledge Search',
      description: query
        ? `User searched for: "${query}" but did not find a suitable answer in the knowledge base.\n\nPlease provide assistance with this query.`
        : 'Support request created from Knowledge Base search.',
    });
    setTicketModalOpen(true);
  };

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 pb-20">
        
        {/* === COMPACT STICKY HEADER (Blue/Sky Theme) === */}
        <div className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-slate-800 transition-all">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              
              {/* Left: Icon & Title */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20 shrink-0">
                  <Search className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 md:flex-none">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    Advanced Search
                  </h1>
                </div>
              </div>

              {/* Center: Large Search Bar */}
              <div className="flex-1 max-w-2xl w-full">
                <div className="relative group">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-sky-500 to-blue-600 rounded-lg blur opacity-20 group-focus-within:opacity-50 transition duration-500"></div>
                  <SearchBar 
                    value={query} 
                    onChange={setQuery}
                    onSearch={handleSearch}
                    placeholder="Search by title, description, or tags..."
                    className="relative w-full !bg-white dark:!bg-slate-900 !border-0 !rounded-lg !py-2.5 !text-sm !shadow-sm" 
                  />
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2">
                {hasSearched && results.length === 0 && (
                  <button
                    onClick={handleCreateTicketFromSearch}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-blue-500/20"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Create Ticket
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ================= MAIN CONTENT ================= */}
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          
          {/* Results */}
          {loading ? (
            <div className="mt-8"><ArticleListSkeleton count={5} /></div>
          ) : hasSearched && results.length === 0 ? (
            <div className="mt-8">
              <EmptyState
                icon={Search}
                title="No results found"
                description={`We couldn't find any articles matching "${query}". Try different keywords or create a support ticket for personalized help.`}
                actionLabel="Create Support Ticket"
                onAction={handleCreateTicketFromSearch}
              />
            </div>
          ) : results.length > 0 ? (
            <div>
              <div className="mb-4 pb-2 border-b border-gray-100 dark:border-slate-800">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Found <span className="font-semibold text-gray-900 dark:text-white">{results.length}</span> result{results.length !== 1 ? 's' : ''} for "<span className="font-medium">{query}</span>"
                </p>
              </div>
              <div className="space-y-3">
                {results.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    viewMode="list"
                    onHelpfulToggle={() => handleSearch()}
                    onClick={() => {
                      setTicketInitial({
                        subject: article.title,
                        description: `User viewed article: ${article.title} (ID: ${article.id}).\n\nArticle content: ${article.content || article.short_description || 'N/A'}`,
                      });
                      setTicketModalOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto text-center py-20">
              <div className="w-20 h-20 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Search className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Start Your Search</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Enter keywords above to search through our knowledge base articles and FAQs.
              </p>
            </div>
          )}
        </div>

        <CreateTicketModal
          isOpen={ticketModalOpen}
          onClose={() => setTicketModalOpen(false)}
          initialSubject={ticketInitial.subject}
          initialDescription={ticketInitial.description}
        />

        <FloatingHelpButton />
      </div>
    </DashboardLayout>
  );
};

export default KnowledgeSearch;
